import { useRef, useCallback } from 'react';
import toast from 'react-hot-toast';

interface FileUploadOptions {
  maxSize: number;
  allowedTypes: string[];
  bytesInMB?: number;

  maxFilesLength?: number;
  multiple?: boolean;

  onFileSelected?: (file: File) => void;
  onFileRemoved?: () => void;

  onFilesSelected?: (newFiles: File[], previousFiles: File[]) => void;
  onMultipleFileRemoved?: (fileName: string, files: File[]) => void;

  getCurrentFiles?: () => File[];

  errorMessages?: {
    sizeError?: string;
    typeError?: string;
    countError?: string;
  };
}

export const useFileUpload = ({
  maxSize,
  allowedTypes,
  bytesInMB = 1048576,
  maxFilesLength = 1,
  multiple = false,
  onFileSelected,
  onFileRemoved,
  onFilesSelected,
  onMultipleFileRemoved,
  getCurrentFiles,
  errorMessages = {},
}: FileUploadOptions) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const showError = useCallback(
    (type: 'sizeError' | 'typeError' | 'countError', defaultMsg: string) => {
      toast.error(errorMessages[type] || defaultMsg);
    },
    [errorMessages]
  );

  const isValidFile = useCallback(
    (file: File): boolean => {
      const isValidSize = file.size / bytesInMB <= maxSize;
      const isValidType = allowedTypes.includes(file.type);

      if (!isValidSize) {
        showError('sizeError', `No puede adjuntar más de ${maxSize}MB`);
        return false;
      }

      if (!isValidType) {
        showError('typeError', 'No puede adjuntar este tipo de archivo');
        return false;
      }

      return true;
    },
    [bytesInMB, maxSize, allowedTypes, showError]
  );

  const handleSingleFile = useCallback(
    (file: File) => {
      if (isValidFile(file)) {
        onFileSelected?.(file);
      }
    },
    [isValidFile, onFileSelected]
  );

  const handleMultipleFiles = useCallback(
    (newFiles: FileList) => {
      const newFileArray = Array.from(newFiles);

      const allTypesValid = newFileArray.every((file) =>
        allowedTypes.includes(file.type)
      );
      if (!allTypesValid) {
        showError('typeError', 'No puede adjuntar este tipo de archivo');
        return;
      }

      const currentFiles = getCurrentFiles?.() || [];
      if (currentFiles.length + newFileArray.length > maxFilesLength) {
        showError(
          'countError',
          `No puede adjuntar más de ${maxFilesLength} archivos`
        );
        return;
      }

      const totalSizeMB =
        [...currentFiles, ...newFileArray].reduce(
          (sum, file) => sum + file.size,
          0
        ) / bytesInMB;

      if (totalSizeMB > maxSize) {
        showError('sizeError', `No puede adjuntar más de ${maxSize}MB`);
        return;
      }

      onFilesSelected?.(newFileArray, currentFiles);
    },
    [
      allowedTypes,
      bytesInMB,
      getCurrentFiles,
      maxFilesLength,
      maxSize,
      onFilesSelected,
      showError,
    ]
  );

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const input = event.target as HTMLInputElement;
      const files = input.files;

      if (!files || !files.length) {
        return;
      }

      if (multiple) {
        handleMultipleFiles(files);
      } else {
        handleSingleFile(files[0]);
      }

      resetInput();
    },
    [multiple, handleMultipleFiles, handleSingleFile]
  );

  const handleDeleteFile = useCallback(
    (fileName?: string) => {
      if (multiple && fileName && onMultipleFileRemoved) {
        const currentFiles = getCurrentFiles?.() || [];
        onMultipleFileRemoved(fileName, currentFiles);
      } else if (!multiple && onFileRemoved) {
        onFileRemoved();
      }

      resetInput();
    },
    [multiple, onFileRemoved, onMultipleFileRemoved, getCurrentFiles]
  );

  return {
    fileInputRef,
    handleFileChange,
    handleDeleteFile,
  };
};
